import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Wifi,
  WifiOff,
  Activity,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Server,
} from 'lucide-react';

const ServiceCard = ({ service, endpoints, logs, uptimeData }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate service-level statistics
  const {
    serviceEndpoints,
    totalEndpoints,
    averageUptime,
    statusColor,
    statusIcon,
    statusText,
  } = useMemo(() => {
    const serviceEndpoints = endpoints.filter((e) => e.service_id === service.id);
    const totalEndpoints = serviceEndpoints.length;

    let operationalCount = 0;
    let totalUptime = 0;
    let hasData = false;

    serviceEndpoints.forEach((endpoint) => {
      const log = logs[endpoint.id];
      if (log) {
        hasData = true;
        if (log.is_success) operationalCount++;
        const uptime = uptimeData[endpoint.id] ?? null;
        if (uptime !== null) totalUptime += uptime;
      }
    });

    const uptimeEndpointsWithData = serviceEndpoints.filter(e => uptimeData[e.id] !== null && uptimeData[e.id] !== undefined).length;
    const averageUptime =
      uptimeEndpointsWithData > 0 ? totalUptime / uptimeEndpointsWithData : null;
    const isServiceOperational = operationalCount === totalEndpoints;
    const hasIssues = operationalCount < totalEndpoints && operationalCount > 0;

    // Determine service status
    let statusColor, statusIcon, statusText;
    if (!hasData) {
      statusColor = 'text-gray-400';
      statusIcon = <Activity className="w-5 h-5 animate-pulse" />;
      statusText = 'Monitoring';
    } else if (isServiceOperational) {
      statusColor = 'text-emerald-400';
      statusIcon = <CheckCircle className="w-5 h-5" />;
      statusText = 'Operational';
    } else if (hasIssues) {
      statusColor = 'text-[#F2C84B]';
      statusIcon = <AlertCircle className="w-5 h-5" />;
      statusText = 'Degraded';
    } else {
      statusColor = 'text-[#B0413E]';
      statusIcon = <AlertCircle className="w-5 h-5" />;
      statusText = 'Down';
    }

    return {
      serviceEndpoints,
      totalEndpoints,
      averageUptime,
      statusColor,
      statusIcon,
      statusText,
    };
  }, [service.id, endpoints, logs, uptimeData]);

  const handleServiceClick = () => {
    navigate(`/admin/service/${service.id}`);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:bg-white/10 transition-all duration-300 hover:shadow-lg hover:shadow-[#2C9C7A]/10">
      {/* Service Header */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {/* Service Icon */}
            <div
              className="flex-shrink-0 p-3 rounded-xl"
              style={{
                backgroundColor: service.color
                  ? `${service.color}20`
                  : 'rgba(139, 92, 246, 0.2)',
              }}
            >
              <Server
                className="w-6 h-6"
                style={{ color: service.color || '#8B5CF6' }}
              />
            </div>

            {/* Service Info */}
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                {service.name}
                <span className="text-sm font-normal text-gray-400">
                  ({totalEndpoints} endpoint{totalEndpoints !== 1 ? 's' : ''})
                </span>
              </h3>
              {service.description && (
                <p className="text-sm text-gray-400 mt-1">{service.description}</p>
              )}
            </div>
          </div>

          {/* Service Stats */}
          <div className="flex items-center space-x-6">
            {/* Uptime */}
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wider">
                Uptime
              </p>
              <p className="text-lg font-semibold text-white">
                {averageUptime !== null ? `${averageUptime.toFixed(1)}%` : '--'}
              </p>
            </div>

            {/* Status */}
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wider">
                Status
              </p>
              <div className={`flex items-center gap-2 ${statusColor}`}>
                {statusIcon}
                <span className="text-sm font-semibold">{statusText}</span>
              </div>
            </div>

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* View Details Button */}
            <button
              onClick={handleServiceClick}
              className="flex items-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
            >
              View Details
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Endpoints List (Expandable) */}
      {isExpanded && (
        <div className="border-t border-white/10 bg-white/5">
          <div className="p-4 space-y-2">
            {serviceEndpoints.length === 0 ? (
              <p className="text-gray-400 text-center py-4">
                No endpoints in this service
              </p>
            ) : (
              serviceEndpoints.map((endpoint) => {
                const log = logs[endpoint.id];
                const isOperational = log?.is_success ?? false;
                const uptime = uptimeData[endpoint.id] ?? null;

                return (
                  <div
                    key={endpoint.id}
                    className="p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      {/* Endpoint Info */}
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {log ? (
                            isOperational ? (
                              <Wifi className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <WifiOff className="w-5 h-5 text-[#B0413E]" />
                            )
                          ) : (
                            <Activity className="w-5 h-5 text-gray-400 animate-pulse" />
                          )}
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-white">
                            {endpoint.label}
                          </h4>
                          <p className="text-xs text-gray-400">{endpoint.url}</p>
                        </div>
                      </div>

                      {/* Endpoint Stats */}
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Response</p>
                          <p className="text-sm font-semibold text-white">
                            {log?.is_success && log.response_time_ms ? `${log.response_time_ms}ms` : '--'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Uptime</p>
                          <p
                            className={`text-sm font-semibold ${
                              uptime === null
                                ? 'text-gray-500'
                                : uptime >= 99
                                ? 'text-emerald-400'
                                : uptime >= 95
                                ? 'text-[#F2C84B]'
                                : 'text-[#B0413E]'
                            }`}
                          >
                            {uptime !== null ? `${uptime.toFixed(1)}%` : '--'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Error Display */}
                    {log && !isOperational && log.error_message && (
                      <div className="mt-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <p className="text-xs text-red-300">
                          <span className="font-medium">Error:</span> {log.error_message}
                        </p>
                        {log.error_type && (
                          <p className="text-xs text-red-400/80 mt-1">
                            Type: {log.error_type}
                          </p>
                        )}
                        {log.status_code && (
                          <p className="text-xs text-red-400/80 mt-1">
                            Status Code: {log.status_code}
                          </p>
                        )}
                        {(log.is_timeout || log.is_connection_error) && (
                          <div className="flex gap-2 mt-1">
                            {log.is_timeout && (
                              <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded">
                                Timeout
                              </span>
                            )}
                            {log.is_connection_error && (
                              <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded">
                                Connection Error
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCard;
